import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Empty,
  Input,
  InputNumber,
  List,
  Modal,
  Pagination,
  Row,
  Segmented,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, LinkOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

import { getCfdiCatalogo, getCfdiCatalogoPdfUrl, getReporteDetalle, postMatchManual } from '../../api';
import type { CategoriaSinCfdi, CfdiCatalogoFiltros, CfdiCatalogoItem, CfdiItem, DetalleItem, OrigenCfdi, Sugerencia } from '../../api';
import { Loader } from '../../components';
import { formatMXN, formatMonto, scoreColor, scoreLabel } from '../../utils/format';
import { sugerenciasComparables } from '../../utils/sugerencias';
import { usePersistedState } from '../../utils/usePersistedState';
import { useEmpresa } from '../../empresa';
import { CATEGORIAS_SIN_CFDI, categoriaSinCfdiMeta } from '../../utils/categoriaSinCfdi';

const AZUL_SIN_CFDI = '#0d6efd';

/** Drena la paginación real del backend en vez de pedir una sola página
 * (evita el límite de 6MB de Lambda). Pide resumen_cuenta+sin_movimiento en
 * paralelo porque el matching manual necesita ambos lados. */
async function fetchTodosNoConciliados(rfc: string, conciliacionId?: string): Promise<DetalleItem[]> {
  const PAGE_SIZE = 200;

  async function drenarTab(tab: 'resumen_cuenta' | 'sin_movimiento', status: string): Promise<DetalleItem[]> {
    let page = 1;
    let items: DetalleItem[] = [];
    for (;;) {
      const rep = await getReporteDetalle(rfc, {
        id: conciliacionId, tab, status, page, page_size: PAGE_SIZE,
      });
      // RFC sin conciliaciones: no hay detalle que drenar.
      if (rep.sin_conciliaciones) break;
      items = items.concat(rep.detalle.items);
      if (items.length >= rep.detalle.total || rep.detalle.items.length === 0) break;
      page++;
    }
    return items;
  }

  const [movimientos, cfdis] = await Promise.all([
    drenarTab('resumen_cuenta', 'SIN_CFDI'),
    drenarTab('sin_movimiento', 'SIN_MOVIMIENTO_BANCO'),
  ]);
  return movimientos.concat(cfdis);
}

const { Title, Paragraph, Text } = Typography;

interface Movimiento {
  id_banco:      string;
  fecha:         string | null;
  descripcion:   string | null;
  deposito:      number | null;
  retiro:        number | null;
  monto:         number;
  rfc_tercero:   string | null;
  banco_fuente:  string | null;
  numero_cuenta: string | null;
  moneda:        string | null;
  sugerencias:   Sugerencia[];
}

interface PendingMatch {
  mov:  Movimiento;
  cfdi: CfdiItem;
  nota: string;
}

/** N movimientos : 1 factura (vs PendingMatch, 1 movimiento : N). */
interface PendingGroup {
  movs: Movimiento[];
  cfdi: CfdiItem;
  nota: string;
}

const PAGE_SIZE = 5;

/** Página del catálogo global; no se drena completo (miles de CFDIs). */
const CATALOGO_PAGE_SIZE = 50;

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const RFC_RE  = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/i;

/** Traduce los filtros del panel a query params. No hay búsqueda libre: UUID →
 * `uuid`, RFC → `rfc`, resto → `folio`; el monto va como rango exacto. */
function construirFiltrosCatalogo(
  texto: string,
  monto: number | null,
  desde: string | null,
  hasta: string | null,
  page: number,
): CfdiCatalogoFiltros {
  const filtros: CfdiCatalogoFiltros = { page, page_size: CATALOGO_PAGE_SIZE };
  const q = texto.trim();
  if (q) {
    if (UUID_RE.test(q))      filtros.uuid  = q;
    else if (RFC_RE.test(q))  filtros.rfc   = q;
    else                      filtros.folio = q;
  }
  if (monto != null) {
    filtros.monto_min = monto;
    filtros.monto_max = monto;
  }
  if (desde) filtros.desde = desde;
  if (hasta) filtros.hasta = hasta;
  return filtros;
}

/** Mapea un item del catálogo al shape de candidato. `razon_social` y
 * `ver_cfdi_url` no existen: quedan vacíos y el PDF se resuelve on-demand. */
function mapCatalogoItem(item: CfdiCatalogoItem): CfdiItem {
  return {
    uuid:         item.uuid,
    rfc_emisor:   item.rfc_emisor,
    rfc_receptor: item.rfc_receptor,
    razon_social: null,
    // El Data API devuelve numeric como string: sin Number() la suma concatena y da NaN.
    total:        item.total == null ? null : Number(item.total),
    fecha:        item.fecha_emision,
    forma_pago:   item.forma_pago,
    metodo_pago:  item.metodo_pago,
    tipo_doc:     item.tipo_doc,
    moneda:       item.moneda,
    ver_cfdi_url: null,
    folio:        item.folio,
    serie:        item.serie,
    origen:       'catalogo',
    // Saldo parcial del CFDI (null => 0).
    saldo_pendiente: Number(item.saldo_pendiente ?? 0),
    pago_parcial:    (item.saldo_pendiente ?? 0) > 0,
    num_pago:        item.num_pago,
  };
}

interface Props {
  m: Pick<Movimiento, 'deposito' | 'retiro'>;
}

function MovTipoTag({ m }: Props) {
  if (m.deposito != null) return <Tag color="green">Depósito</Tag>;
  if (m.retiro != null) return <Tag color="volcano">Retiro</Tag>;
  return null;
}

/** Replica el criterio de desvío de guardar_match_manual: RFC tercero fuera de
 * {rfc_emisor, rfc_receptor} o fecha fuera de [fecha_cfdi - 5 días, + 90 días]. Agrupa por movimiento. */
interface DesviosMovimiento { mov: Movimiento; cfdis: CfdiItem[] }

function obtenerDesviosDeQueue(queueGroups: { mov: Movimiento; entries: PendingMatch[] }[]): DesviosMovimiento[] {
  const resultado: DesviosMovimiento[] = [];
  for (const group of queueGroups) {
    const rfcBanco = (group.mov.rfc_tercero ?? '').trim();
    const fechaBanco = group.mov.fecha ? dayjs(group.mov.fecha) : null;
    const cfdis = group.entries.map(e => e.cfdi).filter(cfdi => {
      const rfcEmisor   = (cfdi.rfc_emisor ?? '').trim();
      const rfcReceptor = (cfdi.rfc_receptor ?? '').trim();
      const desvioRfc = rfcBanco !== '' && (rfcEmisor !== '' || rfcReceptor !== '')
        && rfcBanco !== rfcEmisor && rfcBanco !== rfcReceptor;
      const fechaCfdi = cfdi.fecha ? dayjs(cfdi.fecha) : null;
      const desvioFecha = !!fechaBanco && !!fechaCfdi
        && (fechaBanco.isBefore(fechaCfdi.subtract(5, 'day')) || fechaBanco.isAfter(fechaCfdi.add(90, 'day')));
      return desvioRfc || desvioFecha;
    });
    if (cfdis.length) resultado.push({ mov: group.mov, cfdis });
  }
  return resultado;
}

/** Monto efectivo del movimiento (depósito o retiro). */
function montoMovimiento(m: Movimiento): number {
  return m.deposito ?? m.retiro ?? m.monto;
}

interface ParTraspaso {
  retiro:   Movimiento;
  deposito: Movimiento;
  /** Diferencia de monto o moneda: se avisa pero no bloquea, decide el contador. */
  aviso:    string | null;
}

/** Dirección del movimiento; null si no es claramente retiro o depósito. */
function direccionMov(m: Movimiento): 'retiro' | 'deposito' | null {
  const esDeposito = (m.deposito ?? 0) > 0;
  const esRetiro   = (m.retiro ?? 0) > 0;
  if (esRetiro && !esDeposito) return 'retiro';
  if (esDeposito && !esRetiro) return 'deposito';
  return null;
}

/** Par retiro + depósito para traspaso entre cuentas; null si no son de dirección contraria. */
function obtenerParTraspaso(a: Movimiento, b: Movimiento): ParTraspaso | null {
  const dirA = direccionMov(a);
  const dirB = direccionMov(b);
  if (!dirA || !dirB || dirA === dirB) return null;
  const [retiro, deposito] = dirA === 'retiro' ? [a, b] : [b, a];
  let aviso: string | null = null;
  const monRetiro   = (retiro.moneda ?? '').toUpperCase();
  const monDeposito = (deposito.moneda ?? '').toUpperCase();
  if ((retiro.retiro ?? 0).toFixed(2) !== (deposito.deposito ?? 0).toFixed(2)) {
    aviso = 'El monto del retiro y el del depósito no coinciden.';
  } else if (monRetiro && monDeposito && monRetiro !== monDeposito) {
    aviso = `La moneda del retiro (${monRetiro}) no coincide con la del depósito (${monDeposito}).`;
  }
  return { retiro, deposito, aviso };
}

/** Orden de candidatos a pata 2: 0 = contraria y mismo monto, 1 = contraria, 2 = resto. */
function rangoTraspaso(origen: Movimiento, m: Movimiento): 0 | 1 | 2 {
  const dirOrigen = direccionMov(origen);
  const dir = direccionMov(m);
  if (m.id_banco === origen.id_banco || !dirOrigen || !dir || dir === dirOrigen) return 2;
  return Math.abs(montoMovimiento(m)).toFixed(2) === Math.abs(montoMovimiento(origen)).toFixed(2) ? 0 : 1;
}

export function MatchManual() {
  const { message, modal } = App.useApp();
  const { rfc = '' } = useParams<{ rfc: string }>();
  const [searchParams] = useSearchParams();
  const conciliacionId = searchParams.get('id') || undefined;
  const navigate = useNavigate();

  // Vuelve al listado si cambia el selector global de empresa (no en el mount).
  const { rfc: rfcSeleccionMenu } = useEmpresa();
  const rfcSeleccionMenuRef = useRef(rfcSeleccionMenu);
  useEffect(() => {
    if (rfcSeleccionMenuRef.current === rfcSeleccionMenu) return;
    rfcSeleccionMenuRef.current = rfcSeleccionMenu;
    navigate('/match');
  }, [rfcSeleccionMenu, navigate]);

  const [movimientos, setMovimientos] = useState<Movimiento[]>([]);
  const [sinBanco, setSinBanco]       = useState<CfdiItem[]>([]);
  // Multi-selección. 0/1 = flujo histórico (1 mov : N CFDIs);
  // 2+ = una sola factura (N movs : 1 CFDI).
  const [selectedMovs, setSelectedMovs] = useState<Movimiento[]>([]);
  const [queue, setQueue]             = useState<PendingMatch[]>([]);
  const [pendingGroup, setPendingGroup] = useState<PendingGroup | null>(null);
  const [checkedCfdis, setCheckedCfdis] = useState<Map<string, CfdiItem>>(new Map());
  const [nota, setNota]               = useState('');
  // Pata 1 del traspaso (TC) elegida con el botón TC de la fila.
  const [traspasoOrigen, setTraspasoOrigen] = useState<Movimiento | null>(null);
  const [notaTraspaso, setNotaTraspaso] = useState('');
  const [marcandoTraspaso, setMarcandoTraspaso] = useState(false);
  // Conciliado sin CFDI (préstamo/inversión) pendiente de confirmar en el modal.
  const [pendingSinCfdi, setPendingSinCfdi] = useState<{ movs: Movimiento[]; categoria: CategoriaSinCfdi } | null>(null);
  const [notaSinCfdi, setNotaSinCfdi] = useState('');
  const [marcandoSinCfdi, setMarcandoSinCfdi] = useState(false);
  // Filtros del panel de CFDIs, combinables (antes eran pestañas excluyentes).
  const [textoBusqueda, setTextoBusqueda] = useState('');
  const [montoBusqueda, setMontoBusqueda] = useState<number | null>(null);
  const [dateRange, setDateRange]     = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [loading, setLoading]         = useState(true);
  const [confirming, setConfirming]   = useState(false);
  const [page, setPage]               = useState(1);
  // Fuente de candidatos: conciliación abierta o catálogo global del RFC
  // (cualquier mes, paginado).
  const [origenBusqueda, setOrigenBusqueda] = useState<'conciliacion' | 'catalogo'>('conciliacion');
  const [catalogoItems, setCatalogoItems]     = useState<CfdiItem[]>([]);
  const [catalogoTotal, setCatalogoTotal]     = useState(0);
  const [catalogoPage, setCatalogoPage]       = useState(1);
  const [catalogoLoading, setCatalogoLoading] = useState(false);
  const [movBusqueda, setMovBusqueda] = usePersistedState(`matchManual.${rfc}.movBusqueda`, '');
  const [movMontoMin, setMovMontoMin] = usePersistedState<number | null>(`matchManual.${rfc}.movMontoMin`, null);
  const [movMontoMax, setMovMontoMax] = usePersistedState<number | null>(`matchManual.${rfc}.movMontoMax`, null);
  const [movCuenta, setMovCuenta]     = usePersistedState(`matchManual.${rfc}.movCuenta`, 'TODOS');

  const queuedMovIds  = useMemo(() => {
    const ids = new Set(queue.map(q => q.mov.id_banco));
    for (const m of pendingGroup?.movs ?? []) ids.add(m.id_banco);
    return ids;
  }, [queue, pendingGroup]);
  const queuedCfdiIds = useMemo(() => {
    const ids = new Set(queue.map(q => q.cfdi.uuid.toLowerCase()));
    if (pendingGroup) ids.add(pendingGroup.cfdi.uuid.toLowerCase());
    return ids;
  }, [queue, pendingGroup]);
  // sugerencias no se refetchea tras confirmar otro match — se cruza contra
  // sinBanco (que sí se poda) para no sugerir CFDIs ya conciliados.
  const sinBancoUuids = useMemo(
    () => new Set(sinBanco.map(c => c.uuid.toLowerCase())),
    [sinBanco],
  );

  // Modo "una sola factura": 2+ movimientos seleccionados comparten 1 CFDI.
  const esGrupoMovs = selectedMovs.length >= 2;
  const movPanel    = selectedMovs.length > 0 ? selectedMovs[0] : null;
  const sumaMovs    = useMemo(
    () => selectedMovs.reduce((s, m) => s + montoMovimiento(m), 0),
    [selectedMovs],
  );

  const queueGroups = useMemo(() => {
    const map = new Map<string, { mov: Movimiento; entries: PendingMatch[] }>();
    for (const item of queue) {
      if (!map.has(item.mov.id_banco)) map.set(item.mov.id_banco, { mov: item.mov, entries: [] });
      map.get(item.mov.id_banco)!.entries.push(item);
    }
    return [...map.values()];
  }, [queue]);

  useEffect(() => {
    let cancelled = false;
    fetchTodosNoConciliados(rfc, conciliacionId || undefined)
      .then((items: DetalleItem[]) => {
        if (cancelled) return;
        const cfdis: CfdiItem[] = items
          .filter(i => i.status === 'SIN_MOVIMIENTO_BANCO' && !!i.cfdi?.uuid)
          .map(i => i.cfdi as CfdiItem);
        // Las sugerencias no traen moneda; se toma de la fila CFDI de la conciliación.
        const monedaPorUuid = new Map(cfdis.map(c => [c.uuid.toLowerCase(), c.moneda ?? null]));

        const movs: Movimiento[] = items
          .filter(i => i.status === 'SIN_CFDI' && !!i.banco)
          .map((i, idx) => ({
            id_banco:    i.banco?.id || `${rfc}_${idx}`,
            fecha:       i.banco?.fecha ?? null,
            descripcion: i.banco?.descripcion ?? null,
            deposito:    i.banco?.deposito  ?? null,
            retiro:      i.banco?.retiro    ?? null,
            monto:       i.banco?.monto     ?? 0,
            rfc_tercero: i.banco?.rfc_tercero ?? null,
            banco_fuente:  i.banco?.banco_fuente ?? null,
            numero_cuenta: i.banco?.numero_cuenta ?? null,
            moneda:      i.banco?.moneda ?? null,
            sugerencias: (i.sugerencias || []).slice(0, 3).map(s => ({
              ...s,
              cfdi: { ...s.cfdi, moneda: s.cfdi.moneda ?? monedaPorUuid.get(s.cfdi.uuid.toLowerCase()) ?? null },
            })),
          }));
        setMovimientos(movs);
        setSinBanco(cfdis);
      })
      .catch(err => {
        console.error(err);
        if (!cancelled) message.error('No se Pudo Cargar el Reporte.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [rfc, conciliacionId, message]);

  // Limpia selección y cola al cambiar de empresa/RFC o conciliación.
  useEffect(() => {
    setSelectedMovs([]);
    setPendingGroup(null);
    setQueue([]);
    setCheckedCfdis(new Map());
    setNota('');
    setTraspasoOrigen(null);
    setNotaTraspaso('');
    setPendingSinCfdi(null);
    setNotaSinCfdi('');
    setTextoBusqueda('');
    setMontoBusqueda(null);
    setDateRange(null);
  }, [rfc, conciliacionId]);

  const searchFiltersActive = !!textoBusqueda.trim() || montoBusqueda != null || !!(dateRange && (dateRange[0] || dateRange[1]));

  const searchResults = useMemo<CfdiItem[]>(() => {
    if (!searchFiltersActive) return [];
    const base = sinBanco.filter(c => !queuedCfdiIds.has(c.uuid.toLowerCase()));
    const q = textoBusqueda.trim().toLowerCase();
    const [from, to] = dateRange ?? [null, null];
    const fromStr = from ? from.format('YYYY-MM-DD') : null;
    const toStr   = to   ? to.format('YYYY-MM-DD')   : null;
    return base.filter(c => {
      if (q) {
        const matchTexto =
          c.uuid.toLowerCase().includes(q) ||
          (c.rfc_emisor ?? '').toLowerCase().includes(q) ||
          (c.razon_social ?? '').toLowerCase().includes(q);
        if (!matchTexto) return false;
      }
      if (montoBusqueda != null) {
        const amountStr = Math.abs(c.total ?? 0).toFixed(2);
        if (!amountStr.includes(Math.abs(montoBusqueda).toString())) return false;
      }
      if (fromStr || toStr) {
        const f = c.fecha?.slice(0, 10) ?? null;
        if (!f) return false;
        if (fromStr && f < fromStr) return false;
        if (toStr   && f > toStr)   return false;
      }
      return true;
    }).slice(0, 50);
  }, [searchFiltersActive, textoBusqueda, montoBusqueda, dateRange, sinBanco, queuedCfdiIds]);

  // Catálogo global (modo "Todos los meses"): una página por request, con los
  // mismos filtros del panel.
  useEffect(() => {
    if (origenBusqueda !== 'catalogo' || !movPanel) return;
    let cancelled = false;
    setCatalogoLoading(true);
    getCfdiCatalogo(rfc, construirFiltrosCatalogo(
      textoBusqueda,
      montoBusqueda,
      dateRange?.[0]?.format('YYYY-MM-DD') ?? null,
      dateRange?.[1]?.format('YYYY-MM-DD') ?? null,
      catalogoPage,
    ))
      .then(res => {
        if (cancelled) return;
        setCatalogoItems(res.items.map(mapCatalogoItem));
        setCatalogoTotal(res.total);
      })
      .catch(err => {
        console.error(err);
        if (!cancelled) message.error('No se pudo Consultar el Catálogo de CFDIs.');
      })
      .finally(() => { if (!cancelled) setCatalogoLoading(false); });
    return () => { cancelled = true; };
  }, [origenBusqueda, rfc, textoBusqueda, montoBusqueda, dateRange, catalogoPage, movPanel, message]);

  // Al cambiar cualquier filtro (o el origen), volver a la primera página.
  useEffect(() => { setCatalogoPage(1); }, [textoBusqueda, montoBusqueda, dateRange, origenBusqueda]);

  const cuentasDisponibles = useMemo(() => {
    const vistas = new Map<string, { banco_fuente: string | null; numero_cuenta: string }>();
    for (const m of movimientos) {
      if (m.numero_cuenta && !vistas.has(m.numero_cuenta)) {
        vistas.set(m.numero_cuenta, { banco_fuente: m.banco_fuente, numero_cuenta: m.numero_cuenta });
      }
    }
    return [...vistas.values()];
  }, [movimientos]);

  const filteredMovimientos = useMemo(() => {
    const q = movBusqueda.trim().toLowerCase();
    return movimientos.filter(m => {
      if (q && !(m.descripcion ?? '').toLowerCase().includes(q)) return false;
      const monto = Math.abs(m.deposito ?? m.retiro ?? m.monto);
      if (movMontoMin != null && monto < movMontoMin) return false;
      if (movMontoMax != null && monto > movMontoMax) return false;
      if (movCuenta !== 'TODOS' && m.numero_cuenta !== movCuenta) return false;
      return true;
    });
  }, [movimientos, movBusqueda, movMontoMin, movMontoMax, movCuenta]);

  // Con pata 1 elegida solo se reordena (nunca se oculta); sort estable.
  const movimientosOrdenados = useMemo(() => {
    if (!traspasoOrigen) return filteredMovimientos;
    return [...filteredMovimientos].sort((a, b) => rangoTraspaso(traspasoOrigen, a) - rangoTraspaso(traspasoOrigen, b));
  }, [filteredMovimientos, traspasoOrigen]);

  const movFiltersActive = !!movBusqueda.trim() || movMontoMin != null || movMontoMax != null || movCuenta !== 'TODOS';

  useEffect(() => { setPage(1); }, [movBusqueda, movMontoMin, movMontoMax, movCuenta, traspasoOrigen]);

  function limpiarPanelCfdi() {
    setCheckedCfdis(new Map());
    setNota('');
    setTextoBusqueda('');
    setMontoBusqueda(null);
    setDateRange(null);
  }

  /** Click simple: toggle del único movimiento activo (multi-selección va por checkbox). */
  function selectMov(m: Movimiento) {
    setSelectedMovs(prev => {
      const soloEste = prev.length === 1 && prev[0].id_banco === m.id_banco;
      return soloEste ? [] : [m];
    });
    limpiarPanelCfdi();
  }

  /** Checkbox de la fila: acumula/saca movimientos de la selección múltiple. */
  function toggleMovCheck(m: Movimiento) {
    setSelectedMovs(prev => prev.some(x => x.id_banco === m.id_banco)
      ? prev.filter(x => x.id_banco !== m.id_banco)
      : [...prev, m]);
    limpiarPanelCfdi();
  }

  function toggleCfdi(cfdi: CfdiItem) {
    const id = cfdi.uuid.toLowerCase();
    // Modo N movs : 1 factura: un solo CFDI.
    if (esGrupoMovs) {
      setCheckedCfdis(prev => prev.has(id) ? new Map() : new Map([[id, cfdi]]));
      return;
    }
    setCheckedCfdis(prev => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else              next.set(id, cfdi);
      return next;
    });
  }

  /** Cambia el origen; conserva la selección para poder mezclar ambos orígenes. */
  function cambiarOrigen(v: OrigenCfdi) {
    setOrigenBusqueda(v);
    setCatalogoPage(1);
  }

  /** Abre el PDF; los items de catálogo no traen URL y se resuelve on-demand. */
  async function abrirPdfCfdi(cfdi: CfdiItem) {
    if (cfdi.ver_cfdi_url) {
      window.open(cfdi.ver_cfdi_url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (cfdi.origen !== 'catalogo') return;
    try {
      const { url } = await getCfdiCatalogoPdfUrl(rfc, cfdi.uuid);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error(err);
      message.error('No se Pudo Obtener el PDF del CFDI.');
    }
  }

  function addCheckedToQueue() {
    if (selectedMovs.length === 0 || checkedCfdis.size === 0) return;

    // N movs : 1 factura: selección excluyente y la suma no debe superar el
    // total (mismo guard que el backend).
    if (esGrupoMovs) {
      const cfdi = Array.from(checkedCfdis.values())[0];
      if (!cfdi) return;
      const total = cfdi.total ?? 0;
      if (sumaMovs > total + 0.01) {
        message.error(`La suma de los movimientos (${formatMXN(sumaMovs)}) supera el total de la factura (${formatMXN(total)}).`);
        return;
      }
      setPendingGroup({ movs: selectedMovs, cfdi, nota });
      if (traspasoOrigen && selectedMovs.some(m => m.id_banco === traspasoOrigen.id_banco)) cancelarTraspaso();
      setSelectedMovs([]);
      limpiarPanelCfdi();
      return;
    }

    if (!movPanel || checkedCfdis.size === 0) return;
    const entries = Array.from(checkedCfdis.values());
    const newEntries: PendingMatch[] = entries.map(cfdi => ({
      mov: movPanel, cfdi, nota,
    }));
    setQueue(prev => [...prev, ...newEntries]);
    if (traspasoOrigen?.id_banco === movPanel.id_banco) cancelarTraspaso();
    setCheckedCfdis(new Map());
    setNota('');
    setTextoBusqueda('');
    setMontoBusqueda(null);
    setDateRange(null);
  }

  function removeFromQueue(movId: string, cfdiUuid: string) {
    setQueue(prev => prev.filter(q => !(q.mov.id_banco === movId && q.cfdi.uuid === cfdiUuid)));
  }

  function cancelarTraspaso() {
    setTraspasoOrigen(null);
    setNotaTraspaso('');
  }

  /** 1er click fija la pata 1, otro click en la misma la cancela, click en otra abre la confirmación. */
  function clickTraspaso(m: Movimiento) {
    if (!traspasoOrigen) { setTraspasoOrigen(m); return; }
    if (traspasoOrigen.id_banco === m.id_banco) { cancelarTraspaso(); return; }
    void marcarTraspaso(traspasoOrigen, m);
  }

  async function marcarTraspaso(origen: Movimiento, contraparte: Movimiento) {
    if (marcandoTraspaso) return;
    const par = obtenerParTraspaso(origen, contraparte);
    // Único bloqueo client-side: el backend rechaza dos movimientos de la misma dirección.
    if (!par) {
      modal.warning({
        title: 'Traspaso no válido',
        content: 'Un traspaso entre cuentas requiere un retiro y un depósito. Elige un movimiento de dirección contraria.',
      });
      return;
    }
    const { retiro, deposito, aviso } = par;
    const confirmado = await new Promise<boolean>(resolve => {
      modal.confirm({
        title: 'Marcar como Traspaso entre Cuentas',
        content: `El retiro del ${retiro.fecha || '—'} por ${formatMonto(montoMovimiento(retiro), retiro.moneda)} y el depósito del ${deposito.fecha || '—'} por ${formatMonto(montoMovimiento(deposito), deposito.moneda)} quedarán conciliados como traspaso (TC).${aviso ? ` Atención: ${aviso}` : ''} Puedes deshacerlo desde el detalle del reporte.`,
        okText: 'Marcar como traspaso',
        cancelText: 'Cancelar',
        onOk: () => { resolve(true); },
        onCancel: () => { resolve(false); },
      });
    });
    if (!confirmado) return;

    setMarcandoTraspaso(true);
    try {
      await postMatchManual({
        rfc,
        ids_traspaso: [retiro.id_banco, deposito.id_banco],
        nota: notaTraspaso.trim() || undefined,
      });
    } catch (err: unknown) {
      console.error(err);
      const error = err as { response?: { data?: { error?: string } }; message?: string };
      message.error(error?.response?.data?.error || 'No se pudo marcar el traspaso. Inténtalo de nuevo.');
      setMarcandoTraspaso(false);
      return;
    }
    const ids = new Set([retiro.id_banco, deposito.id_banco]);
    setMovimientos(prev => prev.filter(m => !ids.has(m.id_banco)));
    setSelectedMovs(prev => prev.filter(m => !ids.has(m.id_banco)));
    cancelarTraspaso();
    setMarcandoTraspaso(false);
    message.success('Traspaso entre cuentas registrado.');
  }

  function abrirSinCfdi(movs: Movimiento[], categoria: CategoriaSinCfdi) {
    if (!movs.length || marcandoSinCfdi) return;
    // Los movimientos ya en la fila (o en el grupo N:1) se excluyen, igual que en los botones por fila.
    const libres = movs.filter(m => !queuedMovIds.has(m.id_banco));
    if (!libres.length) {
      message.warning('Los movimientos seleccionados ya están en la fila de matches.');
      return;
    }
    const excluidos = movs.length - libres.length;
    if (excluidos) {
      message.info(`${excluidos} movimiento${excluidos > 1 ? 's' : ''} en la fila de matches no se incluye${excluidos > 1 ? 'n' : ''}.`);
    }
    setPendingSinCfdi({ movs: libres, categoria });
    setNotaSinCfdi('');
  }

  function cerrarSinCfdi() {
    if (marcandoSinCfdi) return;
    setPendingSinCfdi(null);
    setNotaSinCfdi('');
  }

  async function confirmarSinCfdi() {
    if (!pendingSinCfdi || marcandoSinCfdi) return;
    const { movs, categoria } = pendingSinCfdi;
    setMarcandoSinCfdi(true);
    try {
      await postMatchManual({
        rfc,
        ids_sin_cfdi: movs.map(m => m.id_banco),
        categoria_sin_cfdi: categoria,
        nota: notaSinCfdi.trim() || undefined,
      });
    } catch (err: unknown) {
      console.error(err);
      const error = err as { response?: { data?: { error?: string } }; message?: string };
      message.error(error?.response?.data?.error || 'No se pudieron conciliar los movimientos. Inténtalo de nuevo.');
      setMarcandoSinCfdi(false);
      return;
    }
    const ids = new Set(movs.map(m => m.id_banco));
    setMovimientos(prev => prev.filter(m => !ids.has(m.id_banco)));
    setSelectedMovs(prev => prev.filter(m => !ids.has(m.id_banco)));
    if (traspasoOrigen && ids.has(traspasoOrigen.id_banco)) cancelarTraspaso();
    setMarcandoSinCfdi(false);
    setPendingSinCfdi(null);
    setNotaSinCfdi('');
    const nombre = categoriaSinCfdiMeta(categoria)?.nombre.toLowerCase() ?? '';
    message.success(movs.length === 1
      ? `Movimiento conciliado como ${nombre}.`
      : `${movs.length} movimientos conciliados como ${nombre}.`);
  }

  async function confirmAll() {
    if ((!queue.length && !pendingGroup) || confirming) return;

    const desvios = obtenerDesviosDeQueue(queueGroups);
    if (desvios.length > 0) {
      const confirmado = await new Promise<boolean>(resolve => {
        modal.confirm({
          title: 'Hay desvíos detectados en la fila',
          content: (
            <div>
              <Paragraph type="warning" style={{ marginBottom: 8 }}>
                Los siguientes movimiento (s) presentan desviaciones en: RFC / FECHA del respectivo movimiento a conciliar:
              </Paragraph>
              {desvios.map(({ mov, cfdis }) => (
                <ul key={mov.id_banco} style={{ margin: '0 0 8px', paddingLeft: 20 }}>
                  <li>Fecha: ({mov.fecha || '—'}) · {formatMXN(montoMovimiento(mov))}{mov.rfc_tercero ? ` · RFC: ${mov.rfc_tercero}` : ''}</li>
                  {cfdis.map(cfdi => (
                    <li key={cfdi.uuid}>ID movimiento: {mov.id_banco}&nbsp; Folio fiscal: {cfdi.uuid}</li>
                  ))}
                </ul>
              ))}
              <Paragraph type="warning" style={{ marginBottom: 12 }}>
                Si continúas, el match se guardará de todas formas.
              </Paragraph>
              <div>Facturas:</div>
              {desvios.flatMap(({ cfdis }) => cfdis).map(cfdi => (
                <ul key={cfdi.uuid} style={{ margin: '4px 0 8px', paddingLeft: 20 }}>
                  <li>RFC EMISOR: {cfdi.rfc_emisor || '—'}</li>
                  <li>RFC RECEPTOR: {cfdi.rfc_receptor || '—'}</li>
                  <li>FECHA: {cfdi.fecha?.slice(0, 10) || '—'}</li>
                </ul>
              ))}
            </div>
          ),
          okText: 'Confirmar de todas formas',
          cancelText: 'Cancelar',
          okButtonProps: { danger: true },
          onOk: () => { resolve(true); },
          onCancel: () => { resolve(false); },
        });
      });
      if (!confirmado) return;
    }

    setConfirming(true);
    let ok = 0;
    let errMsg = '';
    const savedMovIds  = new Set<string>();
    const savedCfdiIds = new Set<string>();
    // 1 CFDI solo (local o catálogo) va 1:1, así el catálogo conserva los pagos parciales;
    // 2+ CFDIs (aunque mezclen orígenes) van en una sola llamada grupal con el origen de cada uno.
    for (const group of queueGroups) {
      try {
        const { entries } = group;
        if (entries.length === 1) {
          const [entry] = entries;
          await postMatchManual({
            rfc,
            id_banco:  group.mov.id_banco,
            uuid_cfdi: entry.cfdi.uuid,
            ...(entry.cfdi.origen === 'catalogo' ? { origen_cfdi: 'catalogo' as const } : {}),
            nota:      entry.nota || undefined,
          });
        } else {
          const origenes: OrigenCfdi[] = entries.map(e => (e.cfdi.origen === 'catalogo' ? 'catalogo' : 'conciliacion'));
          await postMatchManual({
            rfc,
            id_banco:   group.mov.id_banco,
            uuids_cfdi: entries.map(e => e.cfdi.uuid),
            ...(origenes.includes('catalogo') ? { origenes_cfdi: origenes } : {}),
            // Nota: se manda la primera no vacía — el backend persiste una
            // sola nota para el grupo completo, no una por CFDI.
            nota:       entries.find(e => e.nota)?.nota || undefined,
          });
        }
        ok += entries.length;
        for (const e of entries) savedCfdiIds.add(e.cfdi.uuid.toLowerCase());
        savedMovIds.add(group.mov.id_banco);
      } catch (err: unknown) {
        console.error(err);
        if (!errMsg) {
          const error = err as { response?: { data?: { error?: string } }; message?: string };
          errMsg = error?.response?.data?.error || error?.message || 'Error al guardar match';
        }
      }
    }
    // N movs : 1 factura: una llamada con `ids_banco` + `uuid_cfdi`.
    if (pendingGroup) {
      try {
        await postMatchManual({
          rfc,
          ids_banco: pendingGroup.movs.map(m => m.id_banco),
          uuid_cfdi: pendingGroup.cfdi.uuid,
          // Catálogo debe resolverse por rfc+uuid en cfdi_catalogo; omitirlo
          // hace que el backend use el default 'conciliacion' y falle.
          ...(pendingGroup.cfdi.origen === 'catalogo' ? { origen_cfdi: 'catalogo' as const } : {}),
          nota:      pendingGroup.nota || undefined,
        });
        ok += pendingGroup.movs.length;
        for (const m of pendingGroup.movs) savedMovIds.add(m.id_banco);
        savedCfdiIds.add(pendingGroup.cfdi.uuid.toLowerCase());
      } catch (err: unknown) {
        console.error(err);
        if (!errMsg) {
          const error = err as { response?: { data?: { error?: string } }; message?: string };
          errMsg = error?.response?.data?.error || error?.message || 'Error al guardar match';
        }
      }
    }
    setMovimientos(prev => prev.filter(m => !savedMovIds.has(m.id_banco)));
    setSinBanco(prev => prev.filter(c => !savedCfdiIds.has(c.uuid.toLowerCase())));
    setCatalogoItems(prev => prev.filter(c => !savedCfdiIds.has(c.uuid.toLowerCase())));
    // Poda del queue por CFDI guardado, no solo por movimiento: si un grupo
    // tenía 2+ entries y sólo se persistió una (falló la 2ª), el reintento
    // debe reenviar únicamente las no guardadas, no la ya persistida.
    setQueue(prev => prev.filter(q =>
      !savedMovIds.has(q.mov.id_banco) &&
      !savedCfdiIds.has(q.cfdi.uuid.toLowerCase()),
    ));
    setPendingGroup(null);
    setSelectedMovs([]);
    setConfirming(false);
    if (ok > 0) {
      message.success(`${ok} match${ok !== 1 ? 'es' : ''} guardado${ok !== 1 ? 's' : ''}.`);
    }
    if (errMsg) {
      message.error(errMsg);
    }
  }

  if (loading) return <Loader tip="Cargando Movimientos..." />;

  const paginated = movimientosOrdenados.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const totalMovsConfirm     = queueGroups.length + (pendingGroup?.movs.length ?? 0);
  const totalFacturasConfirm = queue.length + (pendingGroup ? 1 : 0);
  const totalMatchesConfirm  = queue.length + (pendingGroup?.movs.length ?? 0);


  return (
    <div>
      <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate('/match')} style={{ paddingLeft: 0, marginBottom: 12 }}>
        Volver a Conciliación Manual
      </Button>
      <Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>
        Conciliación Manual — <Tag style={{ fontFamily: 'monospace' }}>{rfc}</Tag>
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 20 }}>
        Selecciona un movimiento, marca uno o varios CFDIs en el checkbox, agrégalos a la fila y confirma todo al final. Para conciliar varios movimientos contra una sola factura, marca 2 o más movimientos con el checkbox. Para un traspaso entre cuentas, usa el botón TC en los dos movimientos. Para conciliar sin CFDI un préstamo o una inversión, usa los botones PR o IN del movimiento, o márcalos con el checkbox para hacerlo en bloque.
      </Paragraph>
      <Text style={{ fontSize: 12, display: 'block', textAlign: 'right', marginBottom: 16, color: 'rgba(0, 0, 0, 0.88)' }}>
        <Tag style={{ color: '#198754', borderColor: '#198754', background: '#fff', marginRight: 4 }}>TC</Tag>Traspaso entre cuentas
        {CATEGORIAS_SIN_CFDI.map(c => (
          <span key={c.value}>
            &nbsp;·&nbsp;<Tag style={{ color: AZUL_SIN_CFDI, borderColor: AZUL_SIN_CFDI, background: '#fff', marginRight: 4 }}>{c.codigo}</Tag>{c.nombre}
          </span>
        ))}
      </Text>

      <Row gutter={16} align="top">
        {/* ── Movimientos ── */}
        <Col xs={24} lg={12}>
          <Card
            title={<Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <span>Movimientos sin CFDI</span>
              <Text type="secondary" style={{ fontSize: 12 }}>{movimientos.length} Pendientes</Text>
            </Space>}
            styles={{ body: { padding: 0 } }}
          >
            {movimientos.length === 0 ? (
              <Empty description="No Hay Movimientos Pendientes" style={{ padding: 24 }} />
            ) : (
              <>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Input
                    placeholder="Buscar por descripción..."
                    value={movBusqueda}
                    onChange={e => setMovBusqueda(e.target.value)}
                    allowClear
                    style={{ flex: '1 1 200px' }}
                  />
                  <InputNumber placeholder="Monto mín." value={movMontoMin} onChange={setMovMontoMin} style={{ width: 110 }} />
                  <InputNumber placeholder="Monto máx." value={movMontoMax} onChange={setMovMontoMax} style={{ width: 110 }} />
                  {cuentasDisponibles.length > 0 && (
                    <Select
                      value={movCuenta}
                      onChange={setMovCuenta}
                      style={{ width: 200 }}
                      options={[
                        { value: 'TODOS', label: 'Todas las Cuentas y Bancos' },
                        ...cuentasDisponibles.map(c => ({
                          value: c.numero_cuenta,
                          label: `${c.banco_fuente ?? '—'} — ${c.numero_cuenta}`,
                        })),
                      ]}
                    />
                  )}
                  {movFiltersActive && (
                    <Button onClick={() => { setMovBusqueda(''); setMovMontoMin(null); setMovMontoMax(null); setMovCuenta('TODOS'); }}>
                      Limpiar
                    </Button>
                  )}
                </div>
                {esGrupoMovs && (
                  <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Text style={{ fontSize: 12 }}>{selectedMovs.length} movimientos seleccionados — conciliar sin CFDI como:</Text>
                    {CATEGORIAS_SIN_CFDI.map(c => (
                      <Button
                        key={c.value}
                        size="small"
                        disabled={marcandoSinCfdi}
                        onClick={() => abrirSinCfdi(selectedMovs, c.value)}
                        style={{ color: AZUL_SIN_CFDI, borderColor: AZUL_SIN_CFDI }}
                      >
                        {c.codigo}
                      </Button>
                    ))}
                  </div>
                )}
                {traspasoOrigen && (
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                    <Alert
                      type="success"
                      showIcon
                      message="Elige el movimiento contrario para el traspaso."
                      description={
                        <>
                          <div style={{ fontSize: 12, marginBottom: 8 }}>
                            Pata 1: {direccionMov(traspasoOrigen) === 'retiro' ? 'retiro' : 'depósito'} del {traspasoOrigen.fecha || '—'} por {formatMonto(montoMovimiento(traspasoOrigen), traspasoOrigen.moneda)}. Los movimientos contrarios con el mismo monto aparecen primero.
                          </div>
                          <Input
                            size="small"
                            value={notaTraspaso}
                            onChange={e => setNotaTraspaso(e.target.value)}
                            placeholder="Nota opcional del traspaso..."
                            disabled={marcandoTraspaso}
                          />
                        </>
                      }
                      action={<Button size="small" onClick={cancelarTraspaso} disabled={marcandoTraspaso}>Cancelar</Button>}
                    />
                  </div>
                )}
                {filteredMovimientos.length === 0 ? (
                  <Empty description="Sin resultados para estos filtros" style={{ padding: 24 }} />
                ) : (
                <List
                  dataSource={paginated}
                  renderItem={m => {
                    const isSel = selectedMovs.some(x => x.id_banco === m.id_banco);
                    const inQ   = queuedMovIds.has(m.id_banco);
                    const numSugerencias = sugerenciasComparables(montoMovimiento(m), m.moneda, m.sugerencias).length;
                    const isPata1 = traspasoOrigen?.id_banco === m.id_banco;
                    const isMismoMonto = !!traspasoOrigen && rangoTraspaso(traspasoOrigen, m) === 0;
                    return (
                      <List.Item
                        onClick={() => selectMov(m)}
                        style={{
                          cursor: 'pointer',
                          background: isPata1 ? '#d1e7dd' : inQ ? '#f0fdf4' : isSel ? '#f3eeff' : '#fff',
                          borderLeft: isPata1 || inQ ? '4px solid #198754' : isSel ? '4px solid #6f42c1' : '4px solid transparent',
                          outline: isPata1 ? '2px solid #198754' : undefined,
                          outlineOffset: -2,
                          padding: '12px 16px',
                        }}
                      >
                        <Checkbox
                          checked={isSel}
                          disabled={inQ}
                          onChange={() => toggleMovCheck(m)}
                          onClick={e => e.stopPropagation()}
                          style={{ marginRight: 10 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>{m.fecha || '—'}</Text>
                            <strong>{formatMXN(montoMovimiento(m))}</strong>
                          </Space>
                          <div style={{ marginTop: 4, color: '#343a40', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {m.descripcion || 'Sin Descripción'}
                          </div>
                          <div style={{ marginTop: 4 }}>
                            <MovTipoTag m={m} />
                            <Tooltip title={isPata1 ? 'Cancelar traspaso' : traspasoOrigen ? 'Usar como contraparte del traspaso' : 'Marcar como traspaso entre cuentas'}>
                              <Button
                                size="small"
                                type={isPata1 ? 'primary' : 'default'}
                                disabled={inQ || marcandoTraspaso}
                                onClick={e => { e.stopPropagation(); clickTraspaso(m); }}
                                style={inQ ? { marginInlineEnd: 8 } : isPata1
                                  ? { background: '#198754', borderColor: '#198754', marginInlineEnd: 8 }
                                  : { color: '#198754', borderColor: '#198754', marginInlineEnd: 8 }}
                              >
                                TC
                              </Button>
                            </Tooltip>
                            {CATEGORIAS_SIN_CFDI.map(c => (
                              <Tooltip key={c.value} title={`Conciliar sin CFDI como ${c.nombre.toLowerCase()} (${c.codigo})`}>
                                <Button
                                  size="small"
                                  disabled={inQ || marcandoSinCfdi}
                                  onClick={e => { e.stopPropagation(); abrirSinCfdi([m], c.value); }}
                                  style={inQ ? { marginInlineEnd: 8 } : { color: AZUL_SIN_CFDI, borderColor: AZUL_SIN_CFDI, marginInlineEnd: 8 }}
                                >
                                  {c.codigo}
                                </Button>
                              </Tooltip>
                            ))}
                            {isMismoMonto && <Tag color="green">Mismo monto</Tag>}
                            {cuentasDisponibles.length > 1 && m.numero_cuenta && (
                              <Tag>{m.banco_fuente ?? '—'} · {m.numero_cuenta}</Tag>
                            )}
                            {inQ
                              ? <Tag color="success">✓ En fila</Tag>
                              : numSugerencias > 0 && <Tag color="purple">{numSugerencias} Sugerencia{numSugerencias > 1 ? 's' : ''}</Tag>}
                          </div>
                        </div>
                      </List.Item>
                    );
                  }}
                />
                )}
                {filteredMovimientos.length > 0 && (
                <div style={{ padding: 12, textAlign: 'center', borderTop: '1px solid #f0f0f0' }}>
                  <Pagination
                    current={page}
                    onChange={setPage}
                    total={filteredMovimientos.length}
                    pageSize={PAGE_SIZE}
                    showSizeChanger={false}
                    size="small"
                  />
                </div>
                )}
              </>
            )}
          </Card>
        </Col>

        {/* ── CFDIs ── */}
        <Col xs={24} lg={12}>
          <Card title={selectedMovs.length === 0
            ? 'Seleccionar Movimiento'
            : esGrupoMovs
              ? <Space>{selectedMovs.length} movimientos — <strong>{formatMXN(sumaMovs)}</strong></Space>
              : <Space>{selectedMovs[0].fecha || ''} — {formatMXN(montoMovimiento(selectedMovs[0]))}<MovTipoTag m={selectedMovs[0]} /></Space>}>
            {selectedMovs.length === 0
              ? <Empty description="← Selecciona un Movimiento" />
              : <SearchCfdiPanel
                    movimiento={selectedMovs[0]}
                    esGrupoMovs={esGrupoMovs}
                    sumaMovs={sumaMovs}
                    origenBusqueda={origenBusqueda}
                    onChangeOrigen={cambiarOrigen}
                    searchResults={searchResults}
                    searchFiltersActive={searchFiltersActive}
                    textoBusqueda={textoBusqueda}
                    setTextoBusqueda={setTextoBusqueda}
                    montoBusqueda={montoBusqueda}
                    setMontoBusqueda={setMontoBusqueda}
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                    catalogoItems={catalogoItems}
                    catalogoTotal={catalogoTotal}
                    catalogoPage={catalogoPage}
                    setCatalogoPage={setCatalogoPage}
                    catalogoLoading={catalogoLoading}
                    checkedCfdis={checkedCfdis}
                    onToggle={toggleCfdi}
                    queuedCfdiIds={queuedCfdiIds}
                    sinBancoUuids={sinBancoUuids}
                    nota={nota}
                    setNota={setNota}
                    onAdd={addCheckedToQueue}
                    onVerPdf={abrirPdfCfdi}
                  />}
          </Card>
        </Col>
      </Row>

      <Modal
        title={pendingSinCfdi ? `Conciliar como ${categoriaSinCfdiMeta(pendingSinCfdi.categoria)?.nombre ?? ''}` : ''}
        open={!!pendingSinCfdi}
        onCancel={cerrarSinCfdi}
        onOk={confirmarSinCfdi}
        okText="Aceptar"
        cancelText="Cancelar"
        okButtonProps={{ loading: marcandoSinCfdi, style: { background: AZUL_SIN_CFDI, borderColor: AZUL_SIN_CFDI } }}
        cancelButtonProps={{ disabled: marcandoSinCfdi }}
      >
        {pendingSinCfdi && (
          <>
            <Paragraph>
              {pendingSinCfdi.movs.length === 1 ? 'Este movimiento quedará conciliado' : `Estos ${pendingSinCfdi.movs.length} movimientos quedarán conciliados`} sin
              CFDI como {categoriaSinCfdiMeta(pendingSinCfdi.categoria)?.nombre.toLowerCase()} ({categoriaSinCfdiMeta(pendingSinCfdi.categoria)?.codigo}).
              Cuentan en el % conciliado, pero no en el total SAT. Puedes deshacerlo desde el detalle del reporte.
            </Paragraph>
            <ul style={{ maxHeight: 220, overflowY: 'auto', paddingLeft: 20, margin: '0 0 12px' }}>
              {pendingSinCfdi.movs.map(m => (
                <li key={m.id_banco}>
                  {m.fecha || '—'} · {direccionMov(m) === 'retiro' ? 'Retiro' : 'Depósito'} · <strong>{formatMonto(montoMovimiento(m), m.moneda)}</strong> · {m.descripcion || 'Sin Descripción'}
                </li>
              ))}
            </ul>
            <Input.TextArea
              rows={2}
              value={notaSinCfdi}
              onChange={e => setNotaSinCfdi(e.target.value)}
              placeholder="Nota opcional..."
              disabled={marcandoSinCfdi}
            />
          </>
        )}
      </Modal>

      {(queue.length > 0 || pendingGroup) && (
        <Card
          style={{ marginTop: 20, border: '2px solid #6f42c1' }}
          styles={{ body: { padding: 0 } }}
          title={
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <span>
                Fila de Confirmación — {totalMovsConfirm} movimiento{totalMovsConfirm !== 1 ? 's' : ''}
                {' · '}
                {totalFacturasConfirm} factura{totalFacturasConfirm !== 1 ? 's' : ''}
              </span>
              <Button
                type="primary"
                onClick={confirmAll}
                loading={confirming}
                style={{ background: '#6f42c1', borderColor: '#6f42c1' }}
              >
                {confirming ? 'Confirmando...' : `Confirmar ${totalMatchesConfirm} match${totalMatchesConfirm !== 1 ? 'es' : ''}`}
              </Button>
            </Space>
          }
        >
          {queueGroups.map((group, idx) => {
            const movMonto = group.mov.deposito ?? group.mov.retiro ?? group.mov.monto;
            // Notas de crédito (tipo_doc 'E') restan del total en depósitos y retiros;
            // si todos los CFDIs del movimiento son E (p. ej. una devolución) suman.
            const todasE = group.entries.every(e => e.cfdi.tipo_doc === 'E');
            const cfdiSum = group.entries.reduce((s, e) => {
              const total = e.cfdi.total ?? 0;
              return s + (!todasE && e.cfdi.tipo_doc === 'E' ? -total : total);
            }, 0);
            const diff     = cfdiSum - movMonto;
            const hasDiff  = Math.abs(diff) > 0.01;
            return (
              <div
                key={group.mov.id_banco}
                style={{
                  padding: '14px 20px',
                  borderBottom: idx < queueGroups.length - 1 ? '1px solid #f0f0f0' : 'none',
                }}
              >
                {/* Movimiento banco */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <Text type="secondary" style={{ fontSize: 12, minWidth: 90 }}>{group.mov.fecha || '—'}</Text>
                  <Text style={{ flex: 1, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {group.mov.descripcion || 'Sin Descripción'}
                  </Text>
                  <MovTipoTag m={group.mov} />
                  <strong>{formatMXN(movMonto)}</strong>
                </div>

                {/* Facturas */}
                <div style={{ borderLeft: '3px solid #6f42c1', paddingLeft: 14 }}>
                  {group.entries.map((e, eIdx) => (
                    <div
                      key={e.cfdi.uuid}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '5px 0',
                        borderBottom: eIdx < group.entries.length - 1 ? '1px dashed #f0f0f0' : 'none',
                      }}
                    >
                      <Text style={{ color: '#6f42c1', fontWeight: 700 }}>→</Text>
                      <Text style={{ fontFamily: 'monospace', fontSize: 11, color: '#0d6efd', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {e.cfdi.ver_cfdi_url ? (
                          <a href={e.cfdi.ver_cfdi_url} target="_blank" rel="noopener noreferrer">{e.cfdi.uuid}</a>
                        ) : e.cfdi.origen === 'catalogo' ? (
                          <a onClick={() => abrirPdfCfdi(e.cfdi)}>{e.cfdi.uuid}</a>
                        ) : e.cfdi.uuid}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12, minWidth: 85 }}>{e.cfdi.fecha?.slice(0, 10) || '—'}</Text>
                      {e.nota && <Text type="secondary" style={{ fontSize: 12, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.nota}</Text>}
                      <strong style={{ minWidth: 90, textAlign: 'right' }}>{formatMXN(e.cfdi.total)}</strong>
                      <Button size="small" danger disabled={confirming} onClick={() => removeFromQueue(group.mov.id_banco, e.cfdi.uuid)}>
                        Quitar
                      </Button>
                    </div>
                  ))}

                  {/* Total facturas */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, paddingTop: 8, marginTop: 4 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Total facturas:</Text>
                    <strong>{formatMXN(cfdiSum)}</strong>
                    {hasDiff && (
                      <Tag icon={<WarningOutlined />} color="warning" style={{ marginInlineEnd: 0 }}>
                        {diff > 0 ? '+' : ''}{formatMXN(diff)} vs banco
                      </Tag>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Grupo N movimientos : 1 factura */}
          {pendingGroup && (
            <div
              style={{
                padding: '14px 20px',
                borderTop: queueGroups.length > 0 ? '1px solid #f0f0f0' : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Tag color="purple">{pendingGroup.movs.length} movimientos</Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>→ 1 factura</Text>
              </div>

              <div style={{ borderLeft: '3px solid #6f42c1', paddingLeft: 14, marginBottom: 10 }}>
                {pendingGroup.movs.map(m => (
                  <div key={m.id_banco} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}>
                    <Text type="secondary" style={{ fontSize: 12, minWidth: 90 }}>{m.fecha || '—'}</Text>
                    <Text style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.descripcion || 'Sin Descripción'}
                    </Text>
                    <MovTipoTag m={m} />
                    <strong style={{ minWidth: 90, textAlign: 'right' }}>{formatMXN(montoMovimiento(m))}</strong>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Text style={{ color: '#6f42c1', fontWeight: 700 }}>→</Text>
                <Text style={{ fontFamily: 'monospace', fontSize: 11, color: '#0d6efd', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {pendingGroup.cfdi.ver_cfdi_url ? (
                    <a href={pendingGroup.cfdi.ver_cfdi_url} target="_blank" rel="noopener noreferrer">{pendingGroup.cfdi.uuid}</a>
                  ) : pendingGroup.cfdi.origen === 'catalogo' ? (
                    <a onClick={() => abrirPdfCfdi(pendingGroup.cfdi)}>{pendingGroup.cfdi.uuid}</a>
                  ) : pendingGroup.cfdi.uuid}
                </Text>
                <Text type="secondary" style={{ fontSize: 12, minWidth: 85 }}>{pendingGroup.cfdi.fecha?.slice(0, 10) || '—'}</Text>
                {pendingGroup.nota && <Text type="secondary" style={{ fontSize: 12, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pendingGroup.nota}</Text>}
                <strong style={{ minWidth: 90, textAlign: 'right' }}>{formatMXN(pendingGroup.cfdi.total)}</strong>
                <Button size="small" danger disabled={confirming} onClick={() => setPendingGroup(null)}>
                  Quitar
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

interface SearchPanelProps {
  movimiento:          Movimiento;
  /** N movs : 1 factura: selección excluyente, validada contra la suma. */
  esGrupoMovs:         boolean;
  sumaMovs:            number;
  origenBusqueda:      'conciliacion' | 'catalogo';
  onChangeOrigen:      (v: 'conciliacion' | 'catalogo') => void;
  searchResults:       CfdiItem[];
  searchFiltersActive: boolean;
  textoBusqueda:       string;
  setTextoBusqueda:    (v: string) => void;
  montoBusqueda:       number | null;
  setMontoBusqueda:    (v: number | null) => void;
  dateRange:           [Dayjs | null, Dayjs | null] | null;
  setDateRange:        (v: [Dayjs | null, Dayjs | null] | null) => void;
  catalogoItems:       CfdiItem[];
  catalogoTotal:       number;
  catalogoPage:        number;
  setCatalogoPage:     (v: number) => void;
  catalogoLoading:     boolean;
  checkedCfdis:        Map<string, CfdiItem>;
  onToggle:            (c: CfdiItem) => void;
  queuedCfdiIds:       Set<string>;
  sinBancoUuids:       Set<string>;
  nota:                string;
  setNota:             (v: string) => void;
  onAdd:               () => void;
  onVerPdf:            (c: CfdiItem) => void;
}

function SearchCfdiPanel(p: SearchPanelProps) {
  const esCatalogo = p.origenBusqueda === 'catalogo';
  const showResults = p.searchFiltersActive;
  const mov = p.movimiento;
  const sugerencias = sugerenciasComparables(montoMovimiento(mov), mov.moneda, mov.sugerencias).filter(s => {
    const id = s.cfdi.uuid.toLowerCase();
    return !p.queuedCfdiIds.has(id) && p.sinBancoUuids.has(id);
  });
  // N movimientos : 1 factura — la selección excluyente deja a lo sumo 1 CFDI.
  const cfdiGrupo   = p.esGrupoMovs && p.checkedCfdis.size === 1 ? Array.from(p.checkedCfdis.values())[0] : null;
  const grupoExcede = cfdiGrupo != null && p.sumaMovs > (cfdiGrupo.total ?? 0) + 0.01;

  function cfdiRow(cfdi: CfdiItem, razon?: string, score?: number) {
    const id      = cfdi.uuid.toLowerCase();
    const checked = p.checkedCfdis.has(id);
    const inQ     = p.queuedCfdiIds.has(id);
    return (
      <List.Item
        key={cfdi.uuid}
        onClick={() => !inQ && p.onToggle(cfdi)}
        style={{
          cursor: inQ ? 'default' : 'pointer',
          background: checked ? '#f3eeff' : '#fff',
          borderLeft: checked ? '3px solid #6f42c1' : '3px solid transparent',
          opacity: inQ ? 0.45 : 1,
          padding: '12px 16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
          <Checkbox checked={checked} onChange={() => !inQ && p.onToggle(cfdi)} onClick={e => e.stopPropagation()} disabled={inQ} style={{ marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0, marginLeft: 8 }}>
            <Space>
              <Text style={{ fontFamily: 'monospace', fontSize: 12, color: '#0d6efd' }}>{cfdi.uuid}</Text>
              {cfdi.ver_cfdi_url ? (
                <a href={cfdi.ver_cfdi_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} title="Ver factura PDF">
                  <LinkOutlined />
                </a>
              ) : cfdi.origen === 'catalogo' ? (
                <a onClick={e => { e.stopPropagation(); p.onVerPdf(cfdi); }} title="Ver factura PDF">
                  <LinkOutlined />
                </a>
              ) : null}
              {score != null && (
                <Tag color={scoreColor(score)} style={{ borderColor: scoreColor(score) }}>
                  {score}% — {scoreLabel(score)}
                </Tag>
              )}
            </Space>
            <div style={{ fontSize: 12, color: '#6c757d', marginTop: 2 }}>
              {cfdi.fecha || '—'} · {cfdi.rfc_emisor || '—'}
              {cfdi.razon_social && <span> · {cfdi.razon_social}</span>}
              {cfdi.folio && <span> · Folio {cfdi.folio}</span>}
              {cfdi.tipo_doc && <Tag style={{ marginLeft: 6 }}>{cfdi.tipo_doc}</Tag>}
              {/* Factura con pago parcial y saldo por cobrar. */}
              {(cfdi.saldo_pendiente ?? 0) > 0 && (
                <Tag color="orange" style={{ marginLeft: 6 }}>
                  Pago parcial · saldo {formatMonto(cfdi.saldo_pendiente, cfdi.moneda)}
                  {cfdi.num_pago ? ` (pago ${cfdi.num_pago})` : ''}
                </Tag>
              )}
              {razon && <Text type="secondary" style={{ marginLeft: 8 }}>{razon}</Text>}
            </div>
          </div>
          <div style={{ width: 100, textAlign: 'right' }}>
            <strong>{formatMXN(cfdi.total)}</strong>
          </div>
        </div>
      </List.Item>
    );
  }

  return (
    <>
      <Space direction="vertical" style={{ width: '100%', marginBottom: 12 }}>
        <Segmented
          block
          value={p.origenBusqueda}
          onChange={v => p.onChangeOrigen(v as 'conciliacion' | 'catalogo')}
          options={[
            { label: 'Conciliación actual', value: 'conciliacion' },
            { label: 'Todos los meses', value: 'catalogo' },
          ]}
        />
        {p.esGrupoMovs && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {p.sumaMovs > 0 ? `Modo N movimientos : 1 factura — suma de los movimientos: ${formatMXN(p.sumaMovs)}. ` : ''}
            Seleccione una sola factura por la que cuadre la suma.
          </Text>
        )}
        {esCatalogo && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Busca en el catálogo global del RFC (cualquier mes). Puedes combinar varias facturas, también con las de esta conciliación.
          </Text>
        )}
        <Input
          placeholder={esCatalogo ? 'UUID, RFC contraparte o folio...' : 'UUID, RFC o razón social...'}
          value={p.textoBusqueda}
          onChange={e => p.setTextoBusqueda(e.target.value)}
          allowClear
        />
        <Space.Compact style={{ width: '100%' }}>
          <InputNumber
            placeholder="Monto. Ej. 2600"
            value={p.montoBusqueda}
            onChange={p.setMontoBusqueda}
            style={{ width: '45%' }}
          />
          <DatePicker.RangePicker
            style={{ width: '55%' }}
            value={p.dateRange ?? [null, null]}
            onChange={v => p.setDateRange(v ? [v[0], v[1]] : null)}
            format="YYYY-MM-DD"
          />
        </Space.Compact>
        {p.searchFiltersActive && (
          <Button size="small" onClick={() => { p.setTextoBusqueda(''); p.setMontoBusqueda(null); p.setDateRange(null); }}>
            Limpiar filtros
          </Button>
        )}
      </Space>

      <div style={{ maxHeight: 480, overflowY: 'auto' }}>
        {esCatalogo ? (
          <List
            loading={p.catalogoLoading}
            bordered={false}
            dataSource={p.catalogoItems}
            locale={{ emptyText: <Empty description={p.catalogoLoading ? 'Buscando...' : 'Sin resultados en el catálogo'} /> }}
            renderItem={c => cfdiRow(c)}
          />
        ) : showResults ? (
          p.searchResults.length === 0
            ? <Empty description="Sin resultados para estos filtros" />
            : <List bordered={false} dataSource={p.searchResults} renderItem={c => cfdiRow(c)} />
        ) : (
          sugerencias.length === 0
            ? <Empty description="Sin sugerencias. Use el buscador." />
            : <>
                <Text strong style={{ fontSize: 11, color: '#6c757d', textTransform: 'uppercase', padding: '8px 0', display: 'block' }}>
                  Sugerencias del Algoritmo
                </Text>
                <List bordered={false} dataSource={sugerencias} renderItem={s => cfdiRow(s.cfdi, s.razon, s.score)} />
              </>
        )}
      </div>

      {esCatalogo && p.catalogoTotal > CATALOGO_PAGE_SIZE && (
        <div style={{ textAlign: 'center', paddingTop: 8 }}>
          <Pagination
            current={p.catalogoPage}
            onChange={p.setCatalogoPage}
            total={p.catalogoTotal}
            pageSize={CATALOGO_PAGE_SIZE}
            showSizeChanger={false}
            size="small"
          />
        </div>
      )}

      {p.checkedCfdis.size > 0 && (
        <div style={{ borderTop: '2px solid #6f42c1', background: '#f9f5ff', padding: 12, marginTop: 12 }}>
          <Text strong style={{ color: '#6f42c1', display: 'block', marginBottom: 8 }}>
            {p.esGrupoMovs
              ? '1 factura seleccionada'
              : `${p.checkedCfdis.size} CFDI${p.checkedCfdis.size > 1 ? 's' : ''} seleccionado${p.checkedCfdis.size > 1 ? 's' : ''}`}
          </Text>
          {p.esGrupoMovs && cfdiGrupo && (
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Suma movimientos: {formatMXN(p.sumaMovs)} · Total factura: {formatMXN(cfdiGrupo.total)}
              </Text>
              {grupoExcede && (
                <div>
                  <Text type="danger" style={{ fontSize: 12 }}>
                    La suma de los movimientos supera el total de la factura. Quita movimientos o elige otra factura.
                  </Text>
                </div>
              )}
            </div>
          )}
          <Input.TextArea
            value={p.nota}
            onChange={e => p.setNota(e.target.value)}
            placeholder="Nota opcional para todos..."
            rows={2}
            style={{ marginBottom: 10 }}
          />
          <Button
            type="primary"
            block
            onClick={p.onAdd}
            disabled={grupoExcede}
            style={{ background: '#6f42c1', borderColor: '#6f42c1' }}
          >
            {p.esGrupoMovs ? '+ Agregar 1 factura a la Fila' : `+ Agregar ${p.checkedCfdis.size} a la Fila`}
          </Button>
        </div>
      )}
    </>
  );
}
